/**
 * The bottom of the sidebar: who is signed in, and the two settings that belong
 * to the person rather than to the brand.
 *
 * THE REFERENCE DRAWS THIS EXACTLY: a round avatar, a name in bold over an
 * address in grey, and a sign-out icon on the far right of the same row. Sign
 * out is an icon on the row rather than an item inside the menu because it is
 * the one thing here somebody reaches for without wanting to read anything
 * first, and because putting it in both places would be two ways to do one
 * thing.
 *
 * TWO OF THE TOP BAR'S JOBS LANDED HERE. The person and their sign out came
 * straight across. Second-factor enrolment came too, and that one was nearly
 * lost with the file it lived in, which is why the dialog is now a component of
 * its own.
 *
 * THE THEME USED TO BE THE THIRD. Light, Dark and Follow this machine were
 * three rows in this menu, and a sun-or-moon button beside sign out opened the
 * same three. All of it is gone: the console is light on every machine (the
 * owner, 2026-10-06: "remove the dark mode from the app and the auto theme
 * selection based on os"). There is no preference to store, so there is nothing
 * here to show it with.
 */
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '../lib/cn'
import { label as labelOf } from '../lib/labels'
import { useLogout, useSession } from '../lib/session'
import { Badge } from './Badge'
import { Avatar } from './Identity'
import { Menu, type MenuSection } from './Menu'
import { ProfileDialog } from './ProfileDialog'
import { Spinner } from './Spinner'
import { TotpEnrollDialog } from './TotpEnrollDialog'
import { accountLines } from './sidebar-model'
import { RAIL_IDLE, RAIL_ITEM } from './CollapsedRail'
import { IconDotsVertical, IconKey, IconLogout, IconUser } from './icons'

export function SidebarAccount({ collapsed = false }: { collapsed?: boolean }) {
  const { user, role, isSuperAdmin } = useSession()
  const navigate = useNavigate()
  const logout = useLogout()
  const [open, setOpen] = useState(false)
  const [totpOpen, setTotpOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const lines = accountLines(user)
  if (!user || lines === null) return null

  const signOut = () => {
    setOpen(false)
    logout.mutate(undefined, {
      onSettled: () => navigate('/login', { replace: true }),
    })
  }

  const sections: MenuSection[] = [
    {
      id: 'account',
      items: [
        {
          id: 'profile',
          label: 'Your name and picture',
          icon: <IconUser className="h-4 w-4" />,
          onSelect: () => setProfileOpen(true),
        },
        {
          id: 'totp',
          label: 'Set up an authenticator app',
          icon: <IconKey className="h-4 w-4" />,
          onSelect: () => setTotpOpen(true),
        },
        // On the rail there is no room for the sign-out icon beside the
        // person, so it joins the menu there. Open, it stays an icon on the
        // row and is not repeated here: two ways to do one thing.
        ...(collapsed
          ? [
              {
                id: 'sign-out',
                label: 'Sign out',
                icon: <IconLogout className="h-4 w-4" />,
                onSelect: signOut,
              },
            ]
          : []),
      ],
    },
  ]

  /*
    Upwards, because this band is the last thing in the column. The panel
    renders into the body rather than into this element, so nothing in the
    layout above it can trap it at a level it did not ask for, which is the
    trap the old top bar's menu spent a release in.
  */
  const menu = (
    <Menu
      open={open}
      onClose={() => setOpen(false)}
      triggerRef={triggerRef}
      label="Your account"
      sections={sections}
      placement="top"
      width={264}
      header={
        <>
          <p className="truncate text-sm font-medium text-ink">{lines.primary}</p>
          {lines.secondary === null ? null : <p className="truncate text-xs text-ink-subtle">{lines.secondary}</p>}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Badge tone={role === 'admin' ? 'accent' : 'neutral'}>
              {isSuperAdmin ? 'Super admin' : labelOf.role(role)}
            </Badge>
            {user.tz ? <Badge tone="neutral">{user.tz}</Badge> : null}
          </div>
        </>
      }
    />
  )

  const dialogs = (
    <>
      <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
      <TotpEnrollDialog open={totpOpen} onClose={() => setTotpOpen(false)} />
    </>
  )

  /*
   * THE USER ROW, measured: a hairline above, 16px of air under it, the avatar,
   * the name in semibold over the address in the subtle ink, and the sign-out
   * icon at the far right. Clicking the person opens the account menu.
   *
   * On the rail the row becomes the reference's two items: a kebab that opens
   * the same menu, and the avatar under it.
   */
  if (collapsed) {
    return (
      <div className="mt-2 flex flex-col items-center gap-2 border-t border-line pt-2">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="More for your account"
          title="More for your account"
          className={cn(RAIL_ITEM, RAIL_IDLE)}
        >
          {logout.isPending ? <Spinner size="sm" label={null} /> : <IconDotsVertical className="h-[1.25rem] w-[1.25rem]" />}
        </button>
        <span title={lines.primary} className="grid h-10 w-10 place-items-center">
          <Avatar name={user.name} email={user.email} src={user.avatarUrl} size="md" />
        </span>
        {menu}
        {dialogs}
      </div>
    )
  }

  return (
    <div className="mt-3 border-t border-line pt-4">
      <div className="flex items-center gap-2">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-haspopup="menu"
          aria-expanded={open}
          title="Your account"
          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg p-1 text-left transition hover:bg-surface-hover-strong"
        >
          <Avatar name={user.name} email={user.email} src={user.avatarUrl} size="md" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-ink-secondary">{lines.primary}</span>
            {lines.secondary === null ? null : (
              <span className="block truncate text-sm font-medium text-ink-subtle">{lines.secondary}</span>
            )}
          </span>
        </button>

        <button
          type="button"
          onClick={signOut}
          disabled={logout.isPending}
          aria-label="Sign out"
          title="Sign out"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-subtle transition hover:bg-surface-hover-strong hover:text-ink-secondary disabled:opacity-60"
        >
          {logout.isPending ? <Spinner size="sm" label={null} /> : <IconLogout className="h-4 w-4" />}
        </button>
      </div>
      {menu}
      {dialogs}
    </div>
  )
}
